import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import analytics from '@/lib/mixpanel';
import { toast } from 'sonner';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let hasIdentified = false;
    let sessionCheckCompleted = false;

    // PERFORMANCE: Check cached session first for instant auth
    const checkCachedSession = () => {
      try {
        const cached = localStorage.getItem('sb-session-cache');
        if (cached) {
          const { session: cachedSession, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          
          // Use cache if less than 5 minutes old
          if (age < 5 * 60 * 1000 && cachedSession?.user) {
            console.log('[useAuth] Using cached session for instant load');
            setSession(cachedSession);
            setUser(cachedSession.user);
            setLoading(false);
            return true;
          }
        }
      } catch (e) {
        console.warn('[useAuth] Cache read failed:', e);
      }
      return false;
    };

    // Try cached session first
    const usedCache = checkCachedSession();

    // Reduced timeout for faster fail-over
    const loadingTimeout = setTimeout(() => {
      if (mounted && !sessionCheckCompleted) {
        console.warn('[useAuth] Session check timeout - using fallback');
        if (!usedCache) {
          setLoading(false);
          sessionCheckCompleted = true;
        }
      }
    }, 3000); // Reduced to 3s

    // Check for real session (even if cache was used, verify in background)
    supabase.auth.getSession()
      .then(({ data: { session }, error }) => {
        if (mounted && !sessionCheckCompleted) {
          sessionCheckCompleted = true;
          clearTimeout(loadingTimeout);
          
          if (error) {
            console.error('[useAuth] Session error:', error);
            setSession(null);
            setUser(null);
          } else {
            setSession(session);
            setUser(session?.user ?? null);
            
            // Cache the session
            if (session) {
              try {
                localStorage.setItem('sb-session-cache', JSON.stringify({
                  session,
                  timestamp: Date.now()
                }));
              } catch (e) {
                console.warn('[useAuth] Cache write failed:', e);
              }
            }
            
            if (session?.user && !hasIdentified) {
              hasIdentified = true;
              analytics.identify(session.user.id, {
                email: session.user.email,
              });
            }
          }
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error('[useAuth] Initialization failed:', error);
        if (mounted && !sessionCheckCompleted) {
          sessionCheckCompleted = true;
          clearTimeout(loadingTimeout);
          setLoading(false);
          setUser(null);
          setSession(null);
        }
      });

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Update cache on auth changes
        if (session) {
          try {
            localStorage.setItem('sb-session-cache', JSON.stringify({
              session,
              timestamp: Date.now()
            }));
          } catch (e) {
            console.warn('[useAuth] Cache update failed:', e);
          }
        } else {
          localStorage.removeItem('sb-session-cache');
        }

        // Handle session expiration
        if (event === 'SIGNED_OUT') {
          hasIdentified = false;
          analytics.track('User Signed Out');
          analytics.reset();
        }

        if (event === 'SIGNED_IN' && session?.user && !hasIdentified) {
          hasIdentified = true;
          analytics.identify(session.user.id, {
            email: session.user.email,
          });
          analytics.track('User Signed In');
        }
      }
    );

    // Check and refresh token periodically (every 10 minutes)
    const tokenRefreshInterval = setInterval(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          const expiresAt = session.expires_at;
          const now = Math.floor(Date.now() / 1000);
          
          // Refresh if token expires in less than 10 minutes
          if (expiresAt && expiresAt - now < 600) {
            const { error } = await supabase.auth.refreshSession();
            
            if (error) {
              console.error('Session refresh error:', error);
              toast.error('Your session has expired. Please log in again.');
              await supabase.auth.signOut();
            }
          }
        }
      } catch (error) {
        // Silently handle network errors during token refresh
        console.warn('Token refresh check failed:', error);
      }
    }, 10 * 60 * 1000); // Every 10 minutes (reduced frequency)

    return () => {
      mounted = false;
      clearTimeout(loadingTimeout);
      subscription.unsubscribe();
      clearInterval(tokenRefreshInterval);
    };
  }, []);

  const signOut = async () => {
    analytics.track('User Signed Out');
    analytics.reset();
    await supabase.auth.signOut();
  };

  return { user, session, loading, signOut };
};
